import type { MovementInput } from "../systems/localMovement.js";

export interface Keyboard {
  movement(): MovementInput;
  /** Ability hotkeys pressed since the last call, in press order. */
  takeHotkeys(): string[];
  /** Suspended while a player is dead or typing in chat. */
  setEnabled(enabled: boolean): void;
  isEnabled(): boolean;
  dispose(): void;
}

const MOVE_KEYS = new Set(["KeyW", "KeyA", "KeyS", "KeyD", "Space"]);
const HOTKEYS = new Set(["Digit1", "Digit2", "Digit3", "Digit4"]);

export function createKeyboard(target: Window = window): Keyboard {
  const down = new Set<string>();
  let hotkeys: string[] = [];
  let enabled = true;

  const onKeyDown = (event: KeyboardEvent) => {
    if (!enabled) return;
    if (MOVE_KEYS.has(event.code)) {
      down.add(event.code);
      // Space would otherwise scroll the page behind the canvas.
      event.preventDefault();
    } else if (HOTKEYS.has(event.code) && !event.repeat) {
      hotkeys.push(event.code.replace("Digit", ""));
    }
  };

  const onKeyUp = (event: KeyboardEvent) => {
    down.delete(event.code);
  };

  const onBlur = () => down.clear();

  target.addEventListener("keydown", onKeyDown);
  target.addEventListener("keyup", onKeyUp);
  target.addEventListener("blur", onBlur);

  return {
    movement: () => {
      if (!enabled) return { forward: 0, strafe: 0, jump: false };
      return {
        forward: (down.has("KeyW") ? 1 : 0) - (down.has("KeyS") ? 1 : 0),
        strafe: (down.has("KeyD") ? 1 : 0) - (down.has("KeyA") ? 1 : 0),
        jump: down.has("Space"),
      };
    },
    takeHotkeys: () => {
      const taken = hotkeys;
      hotkeys = [];
      return taken;
    },
    setEnabled: (next) => {
      enabled = next;
      if (!enabled) {
        down.clear();
        hotkeys = [];
      }
    },
    isEnabled: () => enabled,
    dispose: () => {
      target.removeEventListener("keydown", onKeyDown);
      target.removeEventListener("keyup", onKeyUp);
      target.removeEventListener("blur", onBlur);
    },
  };
}
